import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router";

import API_Calls_DataFile from "@/api/calls/API_Calls_DataFile";
import UploadsTable from "@/components/membership/Uploader/UploadsTable";
import Notifications from "@/components/Notifications/Notifications";
import Uploader from "@/components/sharedComponents/Uploader";
import { useUpdateDataContext } from "@/contexts/UpdateDataContext";
import { getValidLocalStorageItem } from "@/utils/util";

function FileSelector() {
  const { isAuthenticated, user } = useAuth0();
  const navigate = useNavigate();
  const { handleAffiliateFileDataChange } = useUpdateDataContext();
  const { deleteUpload, getUploads, uploadFile, getUploadColumns } =
    API_Calls_DataFile();
  const [notification, setNotification] = useState({});
  const [loading, setLoading] = useState(false);
  const [filesAwaitingColumns, setFilesAwaitingColumns] = useState([]);
  const [fetchingUploads, setFetchingUploads] = useState(false);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    fetchUploadedFiles();
    // fetchUploadedFiles/getUploads are recreated each render;
    // intentionally fire only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUploadedFiles = async () => {
    if (isAuthenticated) {
      setFetchingUploads(true);
      const response = await getUploads(user.org_name);
      if (!response.error && response.data) {
        const sortedUploads = response.data.uploads.sort(
          (a, b) => new Date(b.created) - new Date(a.created)
        );
        setFiles(sortedUploads);
      } else {
        setNotification({
          message: "Unable to fetch uploads at this time",
          severity: "error",
          messageDuration: 7000,
        });
      }
      setFetchingUploads(false);
    }
  };

  const handleFileUpload = async (fileToUpload) => {
    setLoading(true);
    const response = await uploadFile(user.org_name, fileToUpload);
    if (!response.error && response.data) {
      fetchUploadedFiles();
      const fileParts = response.data.filename.split("/");
      const [created, filename] = fileParts.slice(-2);
      getAndCacheColumnNames(user.org_name, filename, created);
    } else {
      setNotification({
        title: response.message || "There was a problem uploading this file",
        message: response.details,
        severity: "error",
        messageDuration: 3000,
      });
    }
    setLoading(false);
  };

  const handleFileSelection = async (file) => {
    const columns = getValidLocalStorageItem(file.filename + file.created);
    if (columns) {
      setAffiliateFileContext(file, columns);
    } else {
      // get column names from server
      setFilesAwaitingColumns((prevArray) => [
        ...prevArray,
        file.filename + file.created,
      ]);
      const response = await getUploadColumns(
        user.org_name,
        file.filename,
        file.created
      );
      setAffiliateFileContext(file, response.data);
      localStorage.setItem(
        file.filename + file.created,
        JSON.stringify(response.data)
      );
      setFilesAwaitingColumns((prevArray) =>
        prevArray.filter((item) => item !== file.filename + file.created)
      );
    }
    navigate("/membership/update-data/choose-template");
  };

  const setAffiliateFileContext = (file, columns) => {
    handleAffiliateFileDataChange({
      name: file.filename,
      created: file.created,
      columns,
      size: file.size,
    });
  };

  const getAndCacheColumnNames = async (affiliate, filename, created) => {
    setFilesAwaitingColumns((prevArray) => [...prevArray, filename + created]);
    const response = await getUploadColumns(affiliate, filename, created);
    if (!response.error && response.data) {
      localStorage.setItem(filename + created, JSON.stringify(response.data));
    } else {
      // if we fail to get column names for a file, delete file and set notification
      await deleteUpload(user.org_name, filename, created);
      setFiles((currentFiles) =>
        currentFiles.filter(
          (d) => !(d.filename === filename && d.created === created)
        )
      );
      setNotification({
        message: `There was an error extracting the column headers for ${filename}. Please check the column names and try again.`,
        severity: "error",
        messageDuration: null,
      });
      console.error(
        "Error fetching column names:",
        response.message || "Unknown error"
      );
    }
    setFilesAwaitingColumns((prevArray) =>
      prevArray.filter((item) => item !== filename + created)
    );
  };

  const handleFileDelete = async (file) => {
    const response = await deleteUpload(
      user.org_name,
      file.filename,
      file.created
    );
    if (!response.error) {
      setFiles((currentFiles) =>
        currentFiles.filter(
          (d) => !(d.filename === file.filename && d.created === file.created)
        )
      );
      setNotification({
        message: "File Deleted Successfully",
        severity: "success",
        messageDuration: 3000,
      });
    } else {
      setNotification({
        message: "Error deleting file. Please try again.",
        severity: "error",
        messageDuration: 3000,
      });
      console.error(
        "Error deleting upload:",
        response.message || "Unknown error"
      );
    }
  };

  return (
    <>
      <Uploader handleFileUpload={handleFileUpload} loading={loading} />
      <UploadsTable
        files={files}
        handleFileSelection={handleFileSelection}
        handleFileDelete={handleFileDelete}
        isLoading={fetchingUploads}
        filesAwaitingColumns={filesAwaitingColumns}
      />
      {!!Object.keys(notification).length && (
        <Notifications
          notification={notification}
          setNotification={setNotification}
        />
      )}
    </>
  );
}

export default FileSelector;
